// How often a thing comes back - the one field three sheets share.
//
// Four chips and, under "Some days", a seven-square weekday picker. What is
// NOT here is as deliberate: no monthly, no yearly, no "every other week", no
// end date. The API takes all of them and the server can describe all of them,
// but the bar for this screen is a stranger understanding it in sixty seconds,
// and each of those is a control most people would have to think about. They
// are reachable through the API today and belong on a "more" surface one level
// down when there is one (AGENTS.md, "General-audience defaults").
//
// A rule the chips cannot say is therefore possible - another client, or a
// later phase, can store one. `repeatOf` answers null for those, and this field
// then shows the server's own words for the rule and leaves it alone until a
// chip is pressed. Silently rewriting somebody's monthly rule into "Every day"
// because the editor had no chip for it is the failure this avoids.

import { Chips, DayPicker, FieldGroup, Notice } from './Sheet.tsx';
import { REPEATS, WEEKDAY_INITIALS, WEEKDAY_NAMES } from './edit-model.ts';
import type { Repeat } from './edit-model.ts';

const DAY_OPTIONS = WEEKDAY_INITIALS.map((label, key) => ({
  key,
  label,
  name: WEEKDAY_NAMES[key] ?? '',
}));

interface Props {
  /** Null when the stored rule is one these chips cannot express. */
  repeat: Repeat | null;
  days: readonly number[];
  onRepeat: (repeat: Repeat) => void;
  /**
   * ONE day, toggled - not the whole list.
   *
   * The caller holds the list and updates it from its own previous value, which
   * is the difference between a picker that works and one that keeps only the
   * last day pressed: three presses in the same tick all read the same `days`
   * out of this render's closure, so a computed-here list would drop two of
   * them. Found by pressing Mon, Wed and Fri and getting a Friday item.
   */
  onToggleDay: (day: number) => void;
  /**
   * The stored rule in the server's words, shown only while `repeat` is null -
   * the case where this field is describing a rule rather than setting one.
   */
  currentWords?: string;
  /** Named in the one-off notice, so "just once" says which day it means. */
  onceOn?: string;
}

export function RepeatField({ repeat, days, onRepeat, onToggleDay, currentWords, onceOn }: Props) {
  return (
    <>
      <FieldGroup label="When does it repeat?">
        <Chips options={REPEATS} chosen={repeat === null ? [] : [repeat]} onChoose={onRepeat} />
      </FieldGroup>

      {repeat === null && (
        <Notice>
          This one repeats {currentWords === undefined ? 'in a way' : `"${currentWords}"`}, which is
          not one of the choices above. Leave them alone and it stays exactly as it is.
        </Notice>
      )}

      {repeat === 'some' && (
        <FieldGroup label="Which days?">
          <DayPicker options={DAY_OPTIONS} chosen={days} onChoose={onToggleDay} />
        </FieldGroup>
      )}

      {/* Said exactly where the two ideas are easiest to confuse, and nowhere
          else: "just once" inside a schedule is a real thing, and a stranger's
          first reading of it is "then why is it in a schedule at all". */}
      {repeat === 'once' && (
        <Notice>
          This happens once{onceOn === undefined ? '' : `, on ${onceOn}`}, and then it is done. It
          still belongs to the schedule, so it is counted and kept with everything else in there.
        </Notice>
      )}
    </>
  );
}
